import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import Pill from 'ext-ui/components/Pill/Pill'
import React from 'react'
import { EXTEND_FIELDS } from 'src/app/BulkInvite/bulkInvite.constants'
import { ExtendFieldKey } from 'src/app/BulkInvite/bulkInvite.types'
import * as style from 'src/app/BulkInvite/components/MappingGrid.style'
import { useBulkInvite } from 'src/app/BulkInvite/context/BulkInviteContext'
import { useBulkInviteStrings } from 'src/app/BulkInvite/useBulkInviteStrings'
import Box from 'src/components/Box/Box'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'

const FIELD_LABEL_KEY: Record<
  ExtendFieldKey,
  keyof ReturnType<typeof useBulkInviteStrings>
> = {
  firstName: 'fieldFirstName',
  lastName: 'fieldLastName',
  email: 'fieldEmail',
  phone: 'fieldPhone',
  role: 'fieldRole',
}

interface MappingRow {
  id: string
  fieldKey: ExtendFieldKey
}

// Column header + dropdown + footer chrome the wrapper adds; sized so the grid
// shows every row without an inner scrollbar.
const CHROME_HEIGHT = 116
const ROW_HEIGHT = DataGridRowHeight.Default

/**
 * Maps each Extend field to a backend-detected CSV header (or "Do not import"),
 * rebuilt on the Extend DataGrid (mirrors ReviewTable). The CSV column renders
 * the ext-ui SingleDropdown; its menu is portaled to the document body so the
 * row's clipping doesn't hide it. Each header and each field can be used at
 * most once.
 */
const MappingGrid: React.FC = () => {
  const { headers, mapping, setMapping } = useBulkInvite()
  const strings = useBulkInviteStrings()

  const fieldLabel = (key: ExtendFieldKey): string =>
    strings[FIELD_LABEL_KEY[key]] as string

  const headerOptions = headers.map((header) => ({
    value: header,
    label: header,
  }))

  const handleSelect = (fieldKey: ExtendFieldKey, value: string): void => {
    setMapping(fieldKey, value || null)
  }

  const rows: MappingRow[] = EXTEND_FIELDS.map((field) => ({
    id: field.key,
    fieldKey: field.key,
  }))

  const renderExtendFieldCell = (
    params: GridRenderCellParams<MappingRow>,
  ): React.ReactElement => {
    const field = EXTEND_FIELDS.find((f) => f.key === params.row.fieldKey)
    const fieldPill = field?.required ? (
      <Pill
        className={style.staticPillClassName}
        color="highlight"
        size="small"
      >
        {strings.mappingRequired}
      </Pill>
    ) : (
      <Pill className={style.staticPillClassName} size="small">
        {strings.mappingOptional}
      </Pill>
    )

    return (
      <DataGridCell
        params={params}
        text={
          <span css={style.fieldLabelContent}>
            {fieldLabel(params.row.fieldKey)}
            {field && fieldPill}
          </span>
        }
        bold
      />
    )
  }

  const renderMapsToCell = (
    params: GridRenderCellParams<MappingRow>,
  ): React.ReactElement => {
    const fieldKey = params.row.fieldKey
    const selectedHeader = mapping[fieldKey]
    return (
      <DataGridCellWrapper params={params}>
        <div
          css={style.dropdownCell}
          role="group"
          aria-label={strings.mappingColumnDropdownAriaLabel(
            fieldLabel(fieldKey),
          )}
        >
          <SingleDropdown<string>
            variant="small"
            data-testid={`bulkInviteMapCol-${fieldKey}`}
            options={headerOptions}
            noneLabel={strings.mappingDontImport}
            placeholder={strings.mappingDontImport}
            dropdownContainer={document.body}
            searchable
            selectedValue={
              selectedHeader
                ? { value: selectedHeader, label: selectedHeader }
                : undefined
            }
            onSelect={(selected): void =>
              handleSelect(fieldKey, selected.value)
            }
          />
        </div>
      </DataGridCellWrapper>
    )
  }

  const columns: GridColDef<MappingRow>[] = [
    {
      field: 'mapsTo',
      headerName: strings.mappingMapsToHeader,
      description: strings.mappingMapsToHeaderDescription,
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: renderExtendFieldCell,
    },
    {
      field: 'header',
      headerName: strings.mappingCsvColumnHeader,
      description: strings.mappingCsvColumnHeaderDescription,
      flex: 1,
      minWidth: 260,
      sortable: false,
      renderCell: renderMapsToCell,
    },
  ]

  return (
    <Box noPadding noShadow outlineBorder css={style.gridClip}>
      <DataGrid
        rows={rows}
        columns={columns}
        filtersBody={{}}
        height={CHROME_HEIGHT + rows.length * ROW_HEIGHT}
        rowHeight={ROW_HEIGHT}
        hideFooter
        disableColumnFilter
        disableColumnMenu
        data-testid="bulkInviteMappingGrid"
      />
    </Box>
  )
}

export default MappingGrid
